import { useEffect } from 'react';
import { useAuthStore } from '../features/authentication/store/authStore';
import { useContextStore } from '../stores/contextStore';
import { useDomain } from '../features/tenant/hooks/useDomains';
import { useDomainInfo } from '../features/domain/hooks/useSubjects';
import { useSubjectInfo } from '../features/subject/hooks/useSubjectUsers';
import { usePostHog } from './usePostHog';

/**
 * Hook to sync PostHog user identification and context with authentication state
 * This handles automatic user identification, super properties, and context setting
 *
 * Now uses the official PostHog React hooks for better integration
 */
export const usePostHogTracking = () => {
  const { user, currentTenant, userSession, isAuthenticated } = useAuthStore();
  const { activeTenantId, activeDomainId, activeSubjectId } = useContextStore();
  const { identify, setSuperProperties, setGroup, reset, posthog } = usePostHog();

  // Fetch domain and subject names when IDs are available (requires tenantId for key lookup)
  const { data: domainInfo } = useDomain(activeTenantId || '', activeDomainId || '');
  const { data: domainInfoFromDomain } = useDomainInfo(activeTenantId || '', activeDomainId || '');
  const { data: subjectInfo } = useSubjectInfo(activeTenantId || '', activeSubjectId || '');

  // Derive domain and subject names
  const domainName = domainInfo?.name || domainInfoFromDomain?.name;
  const domainCode = domainInfo?.code || domainInfoFromDomain?.code;
  const subjectName = subjectInfo?.name;
  const subjectCode = subjectInfo?.code;

  // Sync user identification with PostHog
  useEffect(() => {
    if (!posthog) return;

    if (isAuthenticated && user) {
      // Identify user in PostHog with person properties (stable user identity)
      identify(user.id, {
        user_id: user.id,
        email: user.email,
        name: user.name,
        created_at: userSession?.sessionCreatedAt,
        tenant_id: currentTenant?.id || '',
        tenant_name: currentTenant?.name,
        user_roles: userSession?.currentRoles?.join(',') || '',
      });

      // Set super properties for contextual data (session context and navigation state)
      setSuperProperties({
        is_authenticated: true,
        tenant_id: currentTenant?.id || activeTenantId || 'none',
        tenant_name: currentTenant?.name || 'none',
        domain_id: activeDomainId || 'none',
        domain_name: domainName || 'none',
        subject_id: activeSubjectId || 'none',
        subject_name: subjectName || 'none',
      });

      // Set additional context as super properties
      setSuperProperties({
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
      });

      if (currentTenant || activeTenantId) {
        setSuperProperties({
          tenant_id: currentTenant?.id || activeTenantId || '',
          tenant_name: currentTenant?.name || 'unknown',
          tenant_code: currentTenant?.code || 'unknown',
        });
      }

      // Set domain context if available
      if (activeDomainId) {
        setSuperProperties({
          domain_id: activeDomainId,
          domain_name: domainName || 'unknown',
          domain_code: domainCode || 'unknown',
        });
      }

      // Set subject context if available
      if (activeSubjectId) {
        setSuperProperties({
          subject_id: activeSubjectId,
          subject_name: subjectName || 'unknown',
          subject_code: subjectCode || 'unknown',
        });
      }

      // Set PostHog groups for hierarchical analytics
      // Tenant group (always available for authenticated users)
      if (currentTenant?.id || activeTenantId) {
        setGroup('tenant', currentTenant?.id || activeTenantId!, {
          id: currentTenant?.id || activeTenantId!,
          name: currentTenant?.name || 'unknown',
          code: currentTenant?.code || 'unknown',
          type: 'university',
        });
      }

      // Domain group (if domain context is active)
      if (activeDomainId) {
        setGroup('domain', activeDomainId, {
          id: activeDomainId,
          name: domainName || 'unknown',
          code: domainCode || 'unknown',
          parent_tenant: currentTenant?.id || activeTenantId || 'unknown',
        });
      }

      // Subject group (if subject context is active)
      if (activeSubjectId) {
        setGroup('subject', activeSubjectId, {
          id: activeSubjectId,
          name: subjectName || 'unknown',
          code: subjectCode || 'unknown',
          parent_domain: activeDomainId || 'unknown',
          parent_tenant: currentTenant?.id || activeTenantId || 'unknown',
        });
      }
    } else {
      // User logged out - reset PostHog
      reset();
      setSuperProperties({
        is_authenticated: false,
      });
    }
  }, [
    isAuthenticated,
    user,
    currentTenant,
    userSession,
    identify,
    setSuperProperties,
    setGroup,
    reset,
    posthog,
    activeTenantId,
    activeDomainId,
    activeSubjectId,
    domainName,
    domainCode,
    subjectName,
    subjectCode,
  ]);

  // Update super properties when context changes (contextual navigation state)
  useEffect(() => {
    if (!posthog) return;

    if (isAuthenticated) {
      // Update super properties for context changes (WHERE/WHAT they're doing)
      setSuperProperties({
        tenant_id: currentTenant?.id || activeTenantId || 'none',
        tenant_name: currentTenant?.name || 'none',
        domain_id: activeDomainId || 'none',
        domain_name: domainName || 'none',
        subject_id: activeSubjectId || 'none',
        subject_name: subjectName || 'none',
      });

      // Update contexts as well
      if (activeDomainId) {
        setSuperProperties({
          domain_id: activeDomainId,
          domain_name: domainName || 'unknown',
          domain_code: domainCode || 'unknown',
        });
      }

      if (activeSubjectId) {
        setSuperProperties({
          subject_id: activeSubjectId,
          subject_name: subjectName || 'unknown',
          subject_code: subjectCode || 'unknown',
        });
      }

      // Update PostHog groups when context changes
      // Tenant group (always available for authenticated users)
      if (currentTenant?.id || activeTenantId) {
        setGroup('tenant', currentTenant?.id || activeTenantId!, {
          id: currentTenant?.id || activeTenantId!,
          name: currentTenant?.name || 'unknown',
          code: currentTenant?.code || 'unknown',
          type: 'university',
        });
      }

      // Domain group (if domain context is active)
      if (activeDomainId) {
        setGroup('domain', activeDomainId, {
          id: activeDomainId,
          name: domainName || 'unknown',
          code: domainCode || 'unknown',
          parent_tenant: currentTenant?.id || activeTenantId || 'unknown',
        });
      }

      // Subject group (if subject context is active)
      if (activeSubjectId) {
        setGroup('subject', activeSubjectId, {
          id: activeSubjectId,
          name: subjectName || 'unknown',
          code: subjectCode || 'unknown',
          parent_domain: activeDomainId || 'unknown',
          parent_tenant: currentTenant?.id || activeTenantId || 'unknown',
        });
      }
    }
  }, [
    currentTenant,
    activeTenantId,
    activeDomainId,
    activeSubjectId,
    domainName,
    domainCode,
    subjectName,
    subjectCode,
    isAuthenticated,
    setSuperProperties,
    setGroup,
    posthog,
  ]);

  // Return nothing - this hook is purely for side effects
  return null;
};
